import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { easeIn } from 'ol/easing';
import { getVectorContext } from 'ol/render';
import { unByKey as olUnByKey } from 'ol/Observable';
import { Circle as CircleStyle, Stroke, Style } from 'ol/style';
import Fill from 'ol/style/Fill';

import { MapProperty } from 'cadenza/map/map-property';

let key;

export class WorkbookHighlightHandler {

  constructor (map) {
    this._map = map;
  }

  highlightFeatures (features) {
    this.resetHighlighting();
    if (features && features.length) {
      this._getHighlightVectorSource().addFeatures(features);
      flash(this._highlightVectorLayer, features[0], this._map);
    }
  }

  resetHighlighting () {
    this._getHighlightVectorSource().refresh();
    olUnByKey(key);
  }

  _getHighlightVectorSource () {
    if (!this._highlightVectorLayer) {
      this._highlightVectorLayer = createHighlightVectorSource(this._map);
    }
    return this._highlightVectorLayer.getSource();
  }

}

function createHighlightVectorSource (map) {
  const source = new VectorSource();
  const styles = map.get(MapProperty.HIGHLIGHT_STYLES);

  return new VectorLayer({
    id: 'highlightlayer',
    source,
    map,
    style: feature => styles[feature.getGeometry().getType()]
  });
}

const duration = 2000;
const strokeInitialWith = 15;

function flash (vectorLayer, feature, map) {
  const start = Date.now();
  const style = map.get(MapProperty.HIGHLIGHT_STYLES)[feature.getGeometry().getType()];
  const originalFillOpacity = style.getFill().getColor()[3];
  key = vectorLayer.on('postrender', animate);

  function animate (event) {
    animateGeometry(event, start, originalFillOpacity, style, feature.getGeometry().clone(), map, feature.getGeometry().getType());
  }
}

function createAnimatedColor (fillOpacity) {
  return [ 192, 24, 24, fillOpacity ];
}

function createBufferingStyle (style, animatedColor, fillOpacity, geometryType) {
  if (geometryType === 'Point' || geometryType === 'MultiPoint') {
    return new Style({
      image: new CircleStyle({
        fill: new Fill({
          color: animatedColor
        }),
        radius: style.getImage().getRadius(),
        stroke: new Stroke({
          color: animatedColor,
          width: (1 - fillOpacity) * strokeInitialWith
        })
      })
    });
  }
  const bufferingStrokeStyle = style.clone();
  bufferingStrokeStyle.getFill().setColor(animatedColor);
  bufferingStrokeStyle.getStroke().setColor(animatedColor);
  bufferingStrokeStyle.getStroke().setWidth((1 - fillOpacity) * strokeInitialWith);
  return bufferingStrokeStyle;
}

function createAnimatedColorStyle (style, animatedColor, geometryType) {
  const animatedGeometryFillStyle = style.clone();
  if (geometryType === 'Point' || geometryType === 'MultiPoint') {
    animatedGeometryFillStyle.getImage().getFill().setColor(animatedColor);
  } else if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
    animatedGeometryFillStyle.getFill().setColor(animatedColor);
  } else {
    animatedGeometryFillStyle.getStroke().setColor(animatedColor);
  }
  return animatedGeometryFillStyle;
}

function animateGeometry (event, start, originalFillOpacity, style, flashGeom, map, geometryType) {
  const frameState = event.frameState;
  const elapsed = frameState.time - start;
  if (elapsed >= duration) {
    olUnByKey(key);
    return;
  }
  const vectorContext = getVectorContext(event);
  const elapsedRatio = elapsed / duration;
  const fillOpacity = easeIn(1 - elapsedRatio);
  const animatedColor = createAnimatedColor(fillOpacity);
  const bufferingStrokeStyle = createBufferingStyle(style, animatedColor, fillOpacity, geometryType);

  // draw geometry below, it will provide animated stroke(buffer) effect
  vectorContext.setStyle(bufferingStrokeStyle);
  vectorContext.drawGeometry(flashGeom);

  // draw geometry on top to force color animation
  const animatedGeometryFillStyle = createAnimatedColorStyle(style, animatedColor, geometryType);
  vectorContext.setStyle(animatedGeometryFillStyle);
  vectorContext.drawGeometry(flashGeom);

  map.render();
}

